import type { Edge, Node } from "@xyflow/react";
import type { PrepFieldCatalogEntry, PrepGraphEdge, PrepGraphNode, PrepJobGraph } from "../../../types/prep";

export type PrepFlowEdgeData = {
  graphEdge: PrepGraphEdge;
  incomplete?: boolean;
};

export type PrepFlowNodeData = {
  graphNode: PrepGraphNode;
  tested?: boolean;
  error?: boolean;
  stale?: boolean;
};

export function isPrepSourceType(type?: PrepGraphNode["type"]): boolean {
  return type === "DSL_QUERY" || type === "ADAPTOR_QUERY" || type === "OPENAPI_QUERY";
}

export function flowNodeType(type?: PrepGraphNode["type"]): string {
  if (type === "OUTPUT") return "prepOutput";
  if (type === "ADAPTOR_QUERY") return "prepAdaptorQuery";
  if (type === "OPENAPI_QUERY") return "prepOpenApiQuery";
  return "prepDslQuery";
}

export function graphToFlow(
  graph: PrepJobGraph,
  staleNodeIds: string[] = []
): { nodes: Node<PrepFlowNodeData>[]; edges: Edge[] } {
  const staleSet = new Set(staleNodeIds);
  const nodes: Node<PrepFlowNodeData>[] = (graph.nodes ?? []).map((raw) => {
    const n = normalizeGraphNode(raw);
    return {
      id: n.id,
      type: flowNodeType(n.type),
      position: graph.positions?.[n.id] ?? { x: 0, y: 0 },
      deletable: n.type !== "OUTPUT",
      data: {
        graphNode: n,
        tested: Boolean(n.lastTest && !n.lastTest.errorMessage),
        error: Boolean(n.lastTest?.errorMessage),
        stale: staleSet.has(n.id),
      },
    };
  });
  const edges: Edge[] = (graph.edges ?? []).map((e) => {
    const incomplete = isEdgeIncomplete(e, graph);
    return {
      id: e.id,
      source: e.sourceNodeId,
      target: e.targetNodeId,
      type: "prepCorrelation",
      label: e.joinType ?? "INNER",
      deletable: true,
      data: { graphEdge: e, incomplete } satisfies PrepFlowEdgeData,
    };
  });
  return { nodes, edges };
}

function isEdgeIncomplete(edge: PrepGraphEdge, graph: PrepJobGraph): boolean {
  const target = graph.nodes?.find((n) => n.id === edge.targetNodeId);
  if (!isPrepSourceType(target?.type)) {
    return false;
  }
  return !edge.upstreamField?.trim() || !edge.downstreamField?.trim();
}

/** Declared `_source` field paths from a DSL template. */
export function extractDslSourceFields(dsl?: Record<string, unknown> | null): string[] {
  if (!dsl) return [];
  const source = dsl._source;
  if (typeof source === "string" && source.trim()) return [source.trim()];
  if (Array.isArray(source)) {
    return source.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((s) => s.trim());
  }
  if (source && typeof source === "object" && !Array.isArray(source)) {
    const includes = (source as { includes?: unknown }).includes;
    if (typeof includes === "string" && includes.trim()) return [includes.trim()];
    if (Array.isArray(includes)) {
      return includes
        .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
        .map((s) => s.trim());
    }
  }
  return [];
}

export function mergeFieldPaths(...groups: string[][]): string[] {
  return [...new Set(groups.flat())].sort((a, b) => a.localeCompare(b));
}

export function edgeFieldPathsFromNode(
  node: PrepGraphNode | undefined,
  mode: "upstream" | "downstream"
): string[] {
  if (!node) return [];
  const fromTest =
    mode === "upstream"
      ? node.lastTest?.cumulativeFields?.map((f) => f.path) ?? []
      : node.lastTest?.fields?.map((f) => f.path) ?? [];
  return mergeFieldPaths(fromTest, extractDslSourceFields(node.dslTemplate));
}

export function upstreamFieldCatalog(graph: PrepJobGraph, nodeId: string): PrepFieldCatalogEntry[] {
  const seen = new Set<string>();
  const result: PrepFieldCatalogEntry[] = [];
  const visited = new Set<string>();

  const walk = (currentId: string) => {
    const incoming = (graph.edges ?? []).filter((e) => e.targetNodeId === currentId);
    for (const edge of incoming) {
      const sourceId = edge.sourceNodeId;
      if (!visited.add(sourceId)) continue;
      const source = graph.nodes?.find((n) => n.id === sourceId);
      if (isPrepSourceType(source?.type) && source?.lastTest?.cumulativeFields) {
        for (const field of source.lastTest.cumulativeFields) {
          if (seen.add(field.path)) {
            result.push(field);
          }
        }
      }
      walk(sourceId);
    }
  };

  walk(nodeId);
  return result;
}

export function countIncompleteEdges(graph: PrepJobGraph | null): number {
  if (!graph?.edges) return 0;
  return graph.edges.filter((e) => isEdgeIncomplete(e, graph)).length;
}

export function applyGraphNodePatch(graph: PrepJobGraph, node: PrepGraphNode): PrepJobGraph {
  return {
    ...graph,
    nodes: (graph.nodes ?? []).map((n) => (n.id === node.id ? { ...n, ...node } : n)),
  };
}

export function applyGraphEdgePatch(graph: PrepJobGraph, edge: PrepGraphEdge): PrepJobGraph {
  return {
    ...graph,
    edges: (graph.edges ?? []).map((e) => (e.id === edge.id ? { ...e, ...edge } : e)),
  };
}

export function outputTargetIndex(graph: PrepJobGraph | null | undefined): string | undefined {
  const output = graph?.nodes?.find((n) => n.type === "OUTPUT");
  const value = output?.targetIndex?.trim();
  return value || undefined;
}

export function normalizeOutputNode(node: PrepGraphNode): PrepGraphNode {
  if (node.type !== "OUTPUT") return node;
  return {
    ...node,
    environment: node.environment ?? "STAGING",
    refreshMode: node.refreshMode ?? "FULL",
    piiMaskEnabled: node.piiMaskEnabled ?? true,
    skipFailedRecords: node.skipFailedRecords ?? false,
    duplicateRecords: node.duplicateRecords ?? false,
    indexApprovalStatus: node.indexApprovalStatus ?? "PENDING",
    bulkSize: Number.isFinite(Number(node.bulkSize ?? 50)) && Number(node.bulkSize ?? 50) >= 1
      ? Math.floor(Number(node.bulkSize ?? 50))
      : 50,
  };
}

export function normalizeDslNode(node: PrepGraphNode): PrepGraphNode {
  if (node.type !== "DSL_QUERY") return node;
  const bulkSize = Number(node.bulkSize ?? 50);
  return {
    ...node,
    bulkSize: Number.isFinite(bulkSize) && bulkSize >= 1 ? Math.floor(bulkSize) : 50,
  };
}

export function normalizeAdaptorNode(node: PrepGraphNode): PrepGraphNode {
  if (node.type !== "ADAPTOR_QUERY") return node;
  const bulkSize = Number(node.bulkSize ?? 50);
  return {
    ...node,
    bulkSize: Number.isFinite(bulkSize) && bulkSize >= 1 ? Math.floor(bulkSize) : 50,
    watermarkKind: node.watermarkKind ?? "DOCUMENT_ORDER",
  };
}

export function normalizeOpenApiNode(node: PrepGraphNode): PrepGraphNode {
  if (node.type !== "OPENAPI_QUERY") return node;
  const bulkSize = Number(node.bulkSize ?? 50);
  return {
    ...node,
    bulkSize: Number.isFinite(bulkSize) && bulkSize >= 1 ? Math.floor(bulkSize) : 50,
    openApiPathArgs: node.openApiPathArgs ?? {},
  };
}

export function normalizeGraphNode(node: PrepGraphNode): PrepGraphNode {
  return normalizeOpenApiNode(normalizeAdaptorNode(normalizeDslNode(normalizeOutputNode(node))));
}

export function flowToGraph(
  nodes: Node<PrepFlowNodeData>[],
  edges: Edge[],
  prev?: PrepJobGraph
): PrepJobGraph {
  const positions: PrepJobGraph["positions"] = {};
  const graphNodes: PrepGraphNode[] = nodes.map((n) => {
    positions[n.id] = { x: n.position.x, y: n.position.y };
    const node = { ...n.data.graphNode, id: n.id };
    return normalizeGraphNode(node);
  });
  const graphEdges: PrepGraphEdge[] = edges.map((e) => {
    const existing = prev?.edges?.find((x) => x.id === e.id);
    const fromData = (e.data as { graphEdge?: PrepGraphEdge } | undefined)?.graphEdge;
    return {
      id: e.id,
      sourceNodeId: e.source,
      targetNodeId: e.target,
      upstreamField: fromData?.upstreamField ?? existing?.upstreamField,
      downstreamField: fromData?.downstreamField ?? existing?.downstreamField,
      joinType: fromData?.joinType ?? existing?.joinType ?? "INNER",
      filterByUpstreamValues:
        fromData?.filterByUpstreamValues ?? existing?.filterByUpstreamValues ?? false,
    };
  });
  return { nodes: graphNodes, edges: graphEdges, positions };
}

export function newAdaptorNode(): PrepGraphNode {
  return {
    id: crypto.randomUUID(),
    type: "ADAPTOR_QUERY",
    label: "Adaptor",
    bulkSize: 50,
    watermarkKind: "DOCUMENT_ORDER",
    mongoCommand: { find: "collection", filter: {} },
    sqlQuery: "SELECT * FROM table_name",
  };
}

export function newOpenApiNode(): PrepGraphNode {
  return {
    id: crypto.randomUUID(),
    type: "OPENAPI_QUERY",
    label: "OpenAPI Catalog",
    bulkSize: 50,
    openApiPathArgs: {},
  };
}

export function newDslNode(x: number, y: number): PrepGraphNode {
  return {
    id: crypto.randomUUID(),
    type: "DSL_QUERY",
    label: "DSL Query",
    bulkSize: 50,
    dslTemplate: { size: 2, query: { match_all: {} } },
  };
}
